'use client'

import Link from 'next/link'
import { Suspense, useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useBlockPageSidebar } from '~/components/BlockPageLayoutClient'
import FocusModeToggleButton from '~/components/FocusModeToggleButton'
import { CompletionTagSkeleton } from '~/components/blocks/BlockSkeleton'
import CompletionTag from '~/components/blocks/CompletionTag'
import { CourseModule } from '~/payload-types'

export default function BlockPageDesktopSidebar({
  currentModule,
  moduleId,
  courseId,
  basePath,
  blockIdent,
}: {
  currentModule: CourseModule
  moduleId: string
  courseId: string
  basePath: string
  blockIdent: string
}) {
  const { isOpen, setIsOpen } = useBlockPageSidebar()
  const [topOffset, setTopOffset] = useState(72)
  const SIDEBAR_WIDTH_PX = 288
  const TOGGLE_OFFSET_FROM_SIDEBAR_TOP_PX = 88
  const toggleTop = topOffset + TOGGLE_OFFSET_FROM_SIDEBAR_TOP_PX

  useEffect(() => {
    let raf = 0
    const updateTopOffset = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const headerBottom = document.querySelector('header')?.getBoundingClientRect().bottom ?? 0
        setTopOffset(Math.max(0, headerBottom))
      })
    }

    updateTopOffset()
    window.addEventListener('scroll', updateTopOffset, { passive: true })
    window.addEventListener('resize', updateTopOffset, { passive: true })

    const observer = new MutationObserver(updateTopOffset)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-focus-mode', 'data-focus-header'],
    })

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', updateTopOffset)
      window.removeEventListener('resize', updateTopOffset)
      observer.disconnect()
    }
  }, [])

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="hidden lg:flex fixed z-50 bg-primary-100 text-white p-2 rounded-l-md hover:bg-primary-80 transition-all duration-300 shadow-lg"
        style={{
          top: `${toggleTop}px`,
          right: isOpen ? `${SIDEBAR_WIDTH_PX}px` : '0',
        }}
        aria-label="Toggle navigation"
        title={isOpen ? 'Close navigation' : 'Open navigation'}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      {/* Desktop sidebar drawer - pushes content instead of overlay */}
      <nav
        className={`hidden lg:block fixed right-0 w-72 bg-white z-40 transform transition-transform duration-300 ease-in-out overflow-y-auto border-l border-gray-100 bottom-0 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          top: `${topOffset}px`,
          right: isOpen ? '0' : `-${SIDEBAR_WIDTH_PX}px`,
        }}
      >
        <div className="p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Navigation
              </h2>
              <FocusModeToggleButton className="h-7 w-7" />
            </div>
            <div className="space-y-2 text-sm">
              <h3 className="text-base font-semibold text-gray-900">{currentModule.title}</h3>
              {currentModule.units && currentModule.units.length > 0 && (
                <div className="flex flex-col gap-6 mt-2">
                  {currentModule.units.map((modUnit) => {
                    if (typeof modUnit === 'number') return null
                    const unitPath = basePath + `/unit/${modUnit.id}`
                    return (
                      <div key={modUnit.id} className="space-y-2">
                        <Link
                          href={unitPath}
                          className="font-medium text-gray-900 hover:text-primary-80"
                        >
                          {modUnit.title}
                        </Link>
                        {modUnit.components.map((block) => {
                          const href = unitPath + `/block/${block.identifier}`
                          const isActive = block.identifier === blockIdent
                          return (
                            <div
                              key={block.id}
                              className="flex items-start justify-between gap-2 ml-4"
                            >
                              <Link
                                href={href}
                                className={twMerge(
                                  'flex-1 min-w-0 hover:text-primary-80',
                                  isActive ? 'text-primary-100 font-medium' : 'text-gray-700',
                                )}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <span className="flex-1">{block.title}</span>
                                  <span className="text-gray-500 whitespace-nowrap">
                                    {block.time} min
                                  </span>
                                </div>
                              </Link>
                              <div className="flex-shrink-0">
                                <Suspense fallback={<CompletionTagSkeleton />}>
                                  <CompletionTag
                                    unitId={modUnit.id}
                                    moduleId={Number(moduleId)}
                                    courseId={Number(courseId)}
                                    blockIdentifier={block.identifier}
                                    blockTime={block.time}
                                    variant="icon"
                                  />
                                </Suspense>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}
